let shortcuts = [];

/**
 * The definition of a shortcut. Shortcuts are listened for globally on keyup events.
 *
 * @typedef {Object} Shortcut
 * @property {(event: KeyboardEvent) => void} handle - The function to be executed when the callback is pressed
 * @property {String} key - The key associated with the shortcut.
 * @property {String} [metaKey] - The value the metaKey property of the event should have. Replaced with ctrlKey on non-Mac platforms.
 * @property {String} [ctrlKey] - The value the ctrlKey property of the event should have.
 */

/**
 * Add a global listener to a shortcut.
 *
 * @param {Shortcut} shortcut - The shortcut object
 */
export function add(shortcut) {
  // Replace `metaKey` with `ctrlKey` on non-mac platforms
  if (!isMac() && "metaKey" in shortcut) {
    shortcut.ctrlKey = shortcut.metaKey;
    delete shortcut.metaKey;
  }
  shortcuts.push(shortcut);
}

function isMac() {
  return navigator.userAgent.includes("Mac");
}

// Using `keydown` because `keyup` is not trigged
// for any key on MacOS whilst the command key is down.
//
// `keydown` has the advantage of being repeated when
// the key is held down, but we prevent this by
// checking `event.repeat` in the event handler.

document.addEventListener("keydown", (event) => {
  if (event.repeat) return;
  if (shouldSkipEvent(event)) return;

  for (const shortcut of shortcuts) {
    if (doesShortcutMatchEvent(shortcut, event)) {
      event.preventDefault();
      shortcut.handle(event);
    }
  }
});

function shouldSkipEvent(event) {
  if (event.ctrlKey || event.metaKey) {
    return false;
  }

  const target = event.target;
  return target.tagName == "INPUT" || target.tagName == "SELECT" || target.tagName == "TEXTAREA" || target.isContentEditable;
}

function doesShortcutMatchEvent(shortcut, event) {
  return (
    event.key === shortcut.key &&
    (!("metaKey" in shortcut) || event.metaKey === shortcut.metaKey) &&
    (!("ctrlKey" in shortcut) || event.ctrlKey === shortcut.ctrlKey)
  );
}
